import { Alert, Form, Input, Modal, message } from 'antd'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../AuthContext'
import { changeOwnPassword, teamErrorMessage } from './teamApi'

/**
 * VistoeraTASK-00525 — a Managed Account created with "must change password on
 * first sign-in" gets a blocking modal until it sets its own password (calls
 * `POST /saas/auth/change-password`, which verifies the current password, stamps
 * `password_changed_at` and clears the flag). Not closable / no mask-dismiss.
 */
export default function ForcePasswordChangeModal() {
  const { me, refresh } = useAuth()
  const { t } = useTranslation('team')
  const [form] = Form.useForm<{ current: string; next: string; confirm: string }>()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const open = Boolean(me?.user.must_change_password)
  if (!open) return null

  const submit = async () => {
    const v = await form.validateFields()
    setSubmitting(true)
    setError(null)
    try {
      await changeOwnPassword(v.current, v.next)
      form.resetFields()
      void message.success(t('forcePw.done'))
      await refresh()
    } catch (e) {
      setError(teamErrorMessage(e, t('forcePw.failed')))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      open
      closable={false}
      maskClosable={false}
      keyboard={false}
      title={t('forcePw.title')}
      okText={t('forcePw.submit')}
      cancelButtonProps={{ style: { display: 'none' } }}
      confirmLoading={submitting}
      onOk={() => void submit()}
      zIndex={2000}
      data-testid="force-password-change"
    >
      <p className="tm-hint" style={{ marginTop: 0 }}>
        {t('forcePw.intro')}
      </p>
      {error && <Alert type="error" showIcon title={error} style={{ marginBottom: 12 }} />}
      <Form form={form} layout="vertical" autoComplete="off">
        <Form.Item name="current" label={t('forcePw.current')} rules={[{ required: true, message: t('create.passwordRequired') }]}>
          <Input.Password autoComplete="current-password" />
        </Form.Item>
        <Form.Item
          name="next"
          label={t('forcePw.new')}
          rules={[
            { required: true, message: t('create.passwordRequired') },
            { min: 8, message: t('create.passwordHint', { min: 8 }) },
          ]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>
        <Form.Item
          name="confirm"
          label={t('forcePw.confirm')}
          dependencies={['next']}
          rules={[
            { required: true, message: t('create.passwordRequired') },
            ({ getFieldValue }) => ({
              validator: (_, value) =>
                !value || getFieldValue('next') === value ? Promise.resolve() : Promise.reject(new Error(t('forcePw.mismatch'))),
            }),
          ]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>
      </Form>
    </Modal>
  )
}
